import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiFilter, FiEye, FiEdit, FiTrash2 } from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('view'); // view, edit, delete
  const [mechanics, setMechanics] = useState([]);
  const [error, setError] = useState(null);
  const [editForm, setEditForm] = useState({
    status: '',
    mechanicId: '',
    notes: ''
  });
  const [assignForm, setAssignForm] = useState({
    mechanicId: ''
  });

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    'in-progress': 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    canceled: 'bg-red-100 text-red-800',
  };

  const statusLabels = {
    pending: 'Menunggu',
    confirmed: 'Dikonfirmasi',
    'in-progress': 'Sedang Dikerjakan',
    completed: 'Selesai',
    cancelled: 'Dibatalkan',
    canceled: 'Dibatalkan',
  };

  const formatPrice = (price) => {
    if (!price || isNaN(price)) {
      return 'Rp-';
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        ...(filterStatus !== 'all' && { status: filterStatus }),
        ...(searchQuery && { term: searchQuery })
      };

      console.log('Fetching bookings with params:', params);

      // Use the correct API endpoint
      const response = await API.get('/api/booking', { params });
      console.log('Bookings response:', response.data);

      // Format bookings data
      const formattedBookings = response.data.map(booking => {
        // Check for vehicle data
        const vehicleMake = booking.make || '';
        const vehicleModel = booking.model || '';
        const vehicleYear = booking.year || '';
        const vehicleLicensePlate = booking.license_plate || '';

        // Format date properly for display
        let formattedDate = booking.booking_date || booking.date;
        if (formattedDate && typeof formattedDate === 'string' && formattedDate.includes('-')) {
          const dateParts = formattedDate.split('-');
          if (dateParts.length === 3) {
            formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
          }
        }

        // Generate services array
        let services = [];
        let totalPrice = parseFloat(booking.service_price || 0);

        if (booking.all_services && booking.all_services.length > 0) {
          // Use the combined services from backend
          services = booking.all_services;
          console.log(`Booking ${booking.id} has combined services:`, services);

          // If we have total_services_price, use that
          if (booking.total_services_price) {
            totalPrice = parseFloat(booking.total_services_price);
          }
          // Otherwise, if we have all_services_prices, calculate total
          else if (booking.all_services_prices && booking.all_services_prices.length === booking.all_services.length) {
            totalPrice = booking.all_services_prices.reduce((sum, price) => sum + parseFloat(price || 0), 0);
          }
        } else if (booking.service_name) {
          // Single service
          services = [booking.service_name];
        }

        // Determine mechanic name with better fallback
        let mechanicName = 'Belum ditugaskan';
        let mechanicId = null;

        // Check if mechanic_id exists and is not null
        if (booking.mechanic_id) {
          mechanicId = booking.mechanic_id;

          // If mechanic_name exists, use it
          if (booking.mechanic_name) {
            mechanicName = booking.mechanic_name;
          } else {
            // Otherwise use a placeholder with the ID
            mechanicName = `Montir #${booking.mechanic_id}`;
          }
        }

        console.log(`Booking ${booking.id} mechanic:`, {
          mechanic_id: booking.mechanic_id,
          mechanic_name: booking.mechanic_name,
          final_name: mechanicName,
          final_id: mechanicId
        });

        return {
          id: booking.id,
          customerName: booking.customer_name,
          customerId: booking.customer_id,
          phone: booking.customer_phone || '',
          email: booking.customer_email || '',
          service: booking.service_name || 'Layanan tidak tersedia',
          services: services,
          related_bookings: booking.related_bookings || [],
          vehicle: `${vehicleMake} ${vehicleModel} ${vehicleLicensePlate ? `(${vehicleLicensePlate})` : ''}`.trim(),
          vehicleMake: vehicleMake,
          vehicleModel: vehicleModel,
          vehicleYear: vehicleYear,
          vehicleLicensePlate: vehicleLicensePlate,
          vehicleId: booking.vehicle_id,
          date: formattedDate || 'Tanggal tidak tersedia',
          time: booking.booking_time || booking.time || 'Waktu tidak tersedia',
          status: booking.status,
          mechanicId: mechanicId,
          mechanicName: mechanicName,
          notes: booking.notes || '',
          price: totalPrice,
          createdAt: booking.created_at || new Date().toISOString(),
          completedAt: booking.completed_at || '',
          canceledAt: booking.canceled_at || '',
          rawData: booking // Store the original data for debugging
        };
      });

      setBookings(formattedBookings);
      setTotalPages(Math.max(1, Math.ceil(formattedBookings.length / 10))); // At least 1 page
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError('Gagal memuat data booking. Silakan coba lagi nanti.');
      setBookings([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterStatus, searchQuery]);

  useEffect(() => {
    fetchBookings();
    fetchMechanics();
  }, [fetchBookings]);

  const fetchMechanics = async () => {
    try {
      const response = await API.get('/api/mechanic');
      console.log('Mechanics response:', response.data);

      // Make sure we have an array of mechanics with proper structure
      if (Array.isArray(response.data)) {
        setMechanics(response.data);
      } else if (response.data && Array.isArray(response.data.mechanics)) {
        // In case API returns { mechanics: [...] }
        setMechanics(response.data.mechanics);
      } else {
        console.error('Unexpected mechanics data format:', response.data);
        setMechanics([]);
      }
    } catch (error) {
      console.error('Error fetching mechanics:', error);
      setMechanics([]);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    setFilterStatus(e.target.value);
    setCurrentPage(1);
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setModalType('view');
    setIsModalOpen(true);
  };

  const handleEditBooking = (booking) => {
    setSelectedBooking(booking);
    setModalType('edit');

    // Initialize edit form with selected booking data
    setEditForm({
      status: booking.status,
      mechanicId: booking.mechanicId || '',
      notes: booking.notes || ''
    });

    setIsModalOpen(true);
  };

  const handleDeleteBooking = (booking) => {
    setSelectedBooking(booking);
    setModalType('delete');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBooking(null);
  };

  const handleUpdateStatus = async (bookingId, status) => {
    try {
      console.log(`Updating booking ${bookingId} status to ${status}`);

      // Use the correct API endpoint for updating status
      const response = await API.put(`/api/booking/${bookingId}/status`, { status });

      console.log('Update status response:', response.data);

      // Show success message
      alert(`Status booking berhasil diubah menjadi ${statusLabels[status]}`);

      // Refresh bookings list
      fetchBookings();
      closeModal();
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert(`Gagal mengubah status booking: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleAssignMechanic = async (bookingId, mechanicId) => {
    try {
      console.log(`Assigning mechanic ${mechanicId} to booking ${bookingId}`);

      // Use the correct API endpoint for assigning mechanic
      const response = await API.put(`/api/booking/${bookingId}/assign-mechanic`, { mechanic_id: mechanicId });

      console.log('Assign mechanic response:', response.data);

      // Show success message
      alert('Montir berhasil ditugaskan');

      // Refresh bookings list
      fetchBookings();
      closeModal();
    } catch (error) {
      console.error('Error assigning mechanic:', error);
      alert(`Gagal menetapkan montir: ${error.response?.data?.message || error.message}`);
    }
  };

  const confirmDelete = async () => {
    try {
      if (!selectedBooking || !selectedBooking.id) {
        alert('ID booking tidak valid');
        return;
      }

      console.log(`Permanently deleting booking ${selectedBooking.id}`);

      // Use the booking schedule DELETE endpoint to completely remove the booking
      const response = await API.delete(`/api/booking/schedule/${selectedBooking.id}`);

      console.log('Delete booking response:', response.data);

      // Show success message
      alert('Booking berhasil dihapus dari sistem');

      // Refresh bookings list
      fetchBookings();
      closeModal();
    } catch (error) {
      console.error('Error deleting booking:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Server error. Please try again later.';
      alert(`Gagal menghapus booking: ${errorMessage}`);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      // Update booking status if changed
      if (editForm.status !== selectedBooking.status) {
        await handleUpdateStatus(selectedBooking.id, editForm.status);
      }

      // Update mechanic if changed
      if (editForm.mechanicId !== selectedBooking.mechanicId && editForm.mechanicId) {
        await handleAssignMechanic(selectedBooking.id, editForm.mechanicId);
      }

      // Update notes if changed
      if (editForm.notes !== selectedBooking.notes) {
        await API.put(`/api/booking/${selectedBooking.id}/notes`, { notes: editForm.notes });
      }

      // Show success message
      alert('Booking berhasil diperbarui');

      // Refresh bookings list and close modal
      fetchBookings();
      closeModal();
    } catch (error) {
      console.error('Error updating booking:', error);
      alert(`Gagal memperbarui booking: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleAssignFormChange = (e) => {
    const { name, value } = e.target;
    setAssignForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();

    if (!assignForm.mechanicId || !selectedBooking) {
      alert('Silakan pilih montir terlebih dahulu');
      return;
    }

    try {
      await handleAssignMechanic(selectedBooking.id, assignForm.mechanicId);
    } catch (error) {
      console.error('Error in assign mechanic:', error);
    }
  };

  const handleQuickAssignMechanic = async (e) => {
    e.preventDefault();
    const mechanicId = e.target.mechanicId.value;

    if (!mechanicId || !selectedBooking) {
      alert('Silakan pilih montir terlebih dahulu');
      return;
    }

    try {
      await handleAssignMechanic(selectedBooking.id, mechanicId);
    } catch (error) {
      console.error('Error in quick assign mechanic:', error);
    }
  };

  const renderPagination = () => {
    return (
      <div className="flex justify-center mt-6">
        <nav className="flex items-center">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Sebelumnya
          </button>
          <div className="px-4 py-1 border-t border-b border-gray-300 bg-white text-sm text-gray-700">
            Halaman {currentPage} dari {totalPages}
          </div>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Berikutnya
          </button>
        </nav>
      </div>
    );
  };

  const renderViewModal = () => {
    if (!selectedBooking) return null;

    return (
      <div className="bg-white rounded-lg p-5 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-bold text-secondary">Detail Booking</h2>
          <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Header - ID & Status */}
        <div className="flex justify-between items-center mb-3 border-b pb-2">
          <div className="text-xs text-gray-500">
            <span className="font-medium">ID: </span>
            {selectedBooking.id}
          </div>
          <div>
            <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${statusColors[selectedBooking.status]}`}>
              {statusLabels[selectedBooking.status]}
            </span>
          </div>
        </div>

        {/* Accordion Sections */}
        <div className="space-y-2 text-sm">
          {/* Layanan */}
          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-medium flex justify-between">
              <span>Layanan</span>
              <span>{formatPrice(selectedBooking.price)}</span>
            </div>
            <div className="p-3">
              {selectedBooking.services && selectedBooking.services.length > 0 ? (
                <ul className="list-disc list-inside text-sm ml-1 space-y-1">
                  {selectedBooking.services.map((service, index) => (
                    <li key={index} className="text-gray-700">{service}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-700">{selectedBooking.service || 'Layanan tidak tersedia'}</p>
              )}
            </div>
          </div>

          {/* Pelanggan & Kendaraan */}
          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-medium">
              Pelanggan & Kendaraan
            </div>
            <div className="p-3 grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-gray-500">Nama</p>
                <p className="font-medium text-sm">{selectedBooking.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Telepon</p>
                <p className="font-medium text-sm">{selectedBooking.phone || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Kendaraan</p>
                <p className="font-medium text-sm">
                  {selectedBooking.vehicleMake} {selectedBooking.vehicleModel}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Plat Nomor</p>
                <p className="font-medium text-sm">{selectedBooking.vehicleLicensePlate || '-'}</p>
              </div>
            </div>
          </div>

          {/* Jadwal */}
          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-medium">
              Jadwal & Montir
            </div>
            <div className="p-3 grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-gray-500">Tanggal</p>
                <p className="font-medium text-sm">{selectedBooking.date}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Waktu</p>
                <p className="font-medium text-sm">{selectedBooking.time}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-500">Montir</p>
                <p className="font-medium text-sm">
                  {selectedBooking.mechanicName}
                  {selectedBooking.mechanicId ? ` (ID: ${selectedBooking.mechanicId})` : ''}
                </p>
              </div>
            </div>

            {/* Quick Assign Mechanic Form */}
            <div className="bg-gray-50 p-3 border-t">
              <form onSubmit={handleQuickAssignMechanic} className="flex flex-col space-y-2">
                <label className="text-xs font-medium text-gray-700">Tugaskan Montir</label>
                <div className="flex space-x-2">
                  <select
                    name="mechanicId"
                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    defaultValue={selectedBooking.mechanicId || ""}
                  >
                    <option value="">Pilih Montir</option>
                    {mechanics.map(mechanic => (
                      <option key={mechanic.id} value={mechanic.id}>
                        {mechanic.name} - {mechanic.specialization || 'Umum'}
                      </option>
                    ))}
                  </select>
                  <button
                    type="submit"
                    className="bg-primary text-white px-3 py-1 rounded text-sm hover:bg-primary-dark"
                  >
                    Tugaskan
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Catatan */}
          {selectedBooking.notes && (
            <div className="border rounded-md overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 font-medium">
                Catatan
              </div>
              <div className="p-3">
                <p className="text-sm">{selectedBooking.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap justify-between items-center mt-4 pt-2 border-t">
          <div className="flex flex-wrap gap-2 mb-2 sm:mb-0">
            <button
              onClick={() => handleUpdateStatus(selectedBooking.id, 'confirmed')}
              disabled={selectedBooking.status !== 'pending'}
              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              title={selectedBooking.status !== 'pending' ? 'Hanya booking dengan status menunggu yang dapat dikonfirmasi' : ''}
            >
              Konfirmasi
            </button>
            <button
              onClick={() => handleUpdateStatus(selectedBooking.id, 'in-progress')}
              disabled={selectedBooking.status !== 'confirmed'}
              className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
              title={selectedBooking.status !== 'confirmed' ? 'Hanya booking dengan status dikonfirmasi yang dapat diproses' : ''}
            >
              Proses
            </button>
            <button
              onClick={() => handleUpdateStatus(selectedBooking.id, 'completed')}
              disabled={selectedBooking.status !== 'in-progress'}
              className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
              title={selectedBooking.status !== 'in-progress' ? 'Hanya booking dengan status sedang diproses yang dapat diselesaikan' : ''}
            >
              Selesai
            </button>
            <button
              onClick={() => handleUpdateStatus(selectedBooking.id, 'cancelled')}
              disabled={selectedBooking.status === 'completed' || selectedBooking.status === 'cancelled'}
              className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
              title={selectedBooking.status === 'completed' || selectedBooking.status === 'cancelled' ? 'Booking yang sudah selesai atau dibatalkan tidak dapat dibatalkan' : ''}
            >
              Batalkan
            </button>
          </div>
          <button
            onClick={closeModal}
            className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          >
            Tutup
          </button>
        </div>
      </div>
    );
  };

  const renderDeleteModal = () => {
    if (!selectedBooking) return null;

    return (
      <div className="bg-white rounded-lg p-5 max-w-sm w-full">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-bold text-secondary">Hapus Booking Permanen</h2>
          <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <div className="border-b pb-3 mb-3">
          <div className="flex items-center mb-2">
            <div className="p-2 bg-red-100 text-red-500 rounded-full mr-3">
              <FiTrash2 size={18} />
            </div>
            <div>
              <p className="font-medium">{selectedBooking.id}</p>
              <p className="text-sm text-gray-500">{selectedBooking.status}</p>
            </div>
          </div>

          <p className="text-sm text-gray-700">
            Booking berikut akan dihapus secara permanen dari sistem:
          </p>
          <ul className="mt-1 text-sm">
            <li><span className="text-gray-500">Pelanggan:</span> {selectedBooking.customerName}</li>
            <li><span className="text-gray-500">Layanan:</span> {selectedBooking.services && selectedBooking.services.length > 0 ? selectedBooking.services[0] : selectedBooking.service}</li>
            <li><span className="text-gray-500">Tanggal:</span> {selectedBooking.date}, {selectedBooking.time}</li>
          </ul>
        </div>

        <p className="text-sm text-red-500 mb-4">
          PERHATIAN: Tindakan ini tidak dapat dibatalkan. Data booking akan dihapus secara permanen dari database dan tidak akan dapat dipulihkan.
        </p>

        <div className="flex justify-end">
          <button
            onClick={closeModal}
            className="px-3 py-1.5 text-xs bg-gray-300 text-gray-700 rounded-md mr-2 hover:bg-gray-400"
          >
            Batal
          </button>
          <button
            onClick={confirmDelete}
            className="px-3 py-1.5 text-xs bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center"
          >
            <FiTrash2 className="mr-1" size={12} /> Hapus Permanen
          </button>
        </div>
      </div>
    );
  };

  const renderEditModal = () => {
    if (!selectedBooking) return null;

    return (
      <div className="bg-white rounded-lg p-5 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-bold text-secondary">Edit Booking</h2>
          <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleEditSubmit} className="space-y-3 text-sm">
          {/* Header - ID & Info */}
          <div className="flex justify-between items-center mb-2 border-b pb-2">
            <div className="text-xs text-gray-500">
              <span className="font-medium">ID: </span>
              {selectedBooking.id}
            </div>
            <div className="text-xs text-gray-500">
              {selectedBooking.date} · {selectedBooking.time}
            </div>
          </div>

          {/* Informasi dasar (non-editable) */}
          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-medium">
              Informasi Dasar
            </div>
            <div className="p-3 space-y-2">
              <div>
                <p className="text-xs text-gray-500">Layanan</p>
                {selectedBooking.services && selectedBooking.services.length > 0 ? (
                  <div className="text-sm font-medium pl-2">
                    {selectedBooking.services.length > 1 ? (
                      <>
                        {selectedBooking.services[0]}
                        <span className="text-primary">+{selectedBooking.services.length - 1} lainnya</span>
                      </>
                    ) : (
                      selectedBooking.services[0]
                    )}
                  </div>
                ) : (
                  <p className="text-sm font-medium">{selectedBooking.service}</p>
                )}
              </div>

              <div>
                <p className="text-xs text-gray-500">Pelanggan</p>
                <p className="text-sm font-medium">{selectedBooking.customerName} · {selectedBooking.phone || 'No Phone'}</p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Kendaraan</p>
                <p className="text-sm font-medium">
                  {selectedBooking.vehicleMake} {selectedBooking.vehicleModel} · {selectedBooking.vehicleLicensePlate || 'No Plate'}
                </p>
              </div>
            </div>
          </div>

          {/* Fields yang bisa di-edit */}
          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-medium">
              Edit Booking
            </div>
            <div className="p-3 space-y-3">
              <div>
                <label htmlFor="status" className="block text-xs text-gray-500 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={editForm.status}
                  onChange={handleEditFormChange}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="pending">Menunggu</option>
                  <option value="confirmed">Dikonfirmasi</option>
                  <option value="in-progress">Sedang Dikerjakan</option>
                  <option value="completed">Selesai</option>
                  <option value="cancelled">Dibatalkan</option>
                </select>
              </div>

              <div>
                <label htmlFor="mechanicId" className="block text-xs text-gray-500 mb-1">
                  Montir
                </label>
                <select
                  id="mechanicId"
                  name="mechanicId"
                  value={editForm.mechanicId}
                  onChange={handleEditFormChange}
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Pilih Montir</option>
                  {mechanics.length > 0 ? (
                    mechanics.map(mechanic => (
                      <option key={mechanic.id} value={mechanic.id}>
                        {mechanic.name} - {mechanic.specialization || 'Umum'}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>Loading montir...</option>
                  )}
                </select>
                {mechanics.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Gagal memuat daftar montir</p>
                )}
              </div>

              <div>
                <label htmlFor="notes" className="block text-xs text-gray-500 mb-1">
                  Catatan
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={editForm.notes}
                  onChange={handleEditFormChange}
                  rows="2"
                  className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Tambahkan catatan untuk booking ini..."
                ></textarea>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t mt-2">
            <button
              type="button"
              onClick={closeModal}
              className="px-3 py-1.5 text-xs bg-gray-300 text-gray-700 rounded-md mr-2 hover:bg-gray-400"
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              Simpan Perubahan
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderAssignModal = () => {
    if (!selectedBooking) return null;

    return (
      <div className="bg-white rounded-lg p-5 max-w-md w-full">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-bold text-secondary">Tugaskan Montir</h2>
          <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleAssignSubmit} className="space-y-3 text-sm">
          {/* Booking Info */}
          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-medium">
              Informasi Booking
            </div>
            <div className="p-3 space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">ID Booking:</span>
                <span className="font-medium">{selectedBooking.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Pelanggan:</span>
                <span className="font-medium">{selectedBooking.customerName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Layanan:</span>
                <span className="font-medium">
                  {selectedBooking.services && selectedBooking.services.length > 0
                    ? (selectedBooking.services.length > 1
                      ? `${selectedBooking.services[0]} +${selectedBooking.services.length - 1} lainnya`
                      : selectedBooking.services[0])
                    : selectedBooking.service}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Jadwal:</span>
                <span className="font-medium">{selectedBooking.date}, {selectedBooking.time}</span>
              </div>
            </div>
          </div>

          {/* Mechanic Selection */}
          <div className="border rounded-md overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 font-medium">
              Pilih Montir
            </div>
            <div className="p-3">
              <select
                id="mechanicId"
                name="mechanicId"
                value={assignForm.mechanicId}
                onChange={handleAssignFormChange}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Pilih Montir</option>
                {mechanics.length > 0 ? (
                  mechanics.map(mechanic => (
                    <option key={mechanic.id} value={mechanic.id}>
                      {mechanic.name} - {mechanic.specialization || 'Umum'}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading montir...</option>
                )}
              </select>
              {mechanics.length === 0 && (
                <p className="text-xs text-red-500 mt-1">Gagal memuat daftar montir</p>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t mt-2">
            <button
              type="button"
              onClick={closeModal}
              className="px-3 py-1.5 text-xs bg-gray-300 text-gray-700 rounded-md mr-2 hover:bg-gray-400"
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              Tugaskan Montir
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <DashboardLayout userRole="admin" userName="Admin">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Manajemen Booking</h1>
          <p className="text-gray-600">Kelola semua booking pelanggan</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <form onSubmit={handleSearch} className="flex mb-4 md:mb-0">
            <div className="relative">
              <input
                type="text"
                placeholder="Cari nama pelanggan atau layanan..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-l focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <button
              type="submit"
              className="bg-primary text-white px-4 py-2 rounded-r hover:bg-primary-dark"
            >
              Cari
            </button>
          </form>

          <div className="flex items-center">
            <FiFilter className="mr-2 text-gray-500" />
            <select
              value={filterStatus}
              onChange={handleStatusChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">Semua Status</option>
              <option value="pending">Menunggu</option>
              <option value="confirmed">Dikonfirmasi</option>
              <option value="in-progress">Sedang Dikerjakan</option>
              <option value="completed">Selesai</option>
              <option value="cancelled">Dibatalkan</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          <p>{error}</p>
        </div>
      )}

      {/* Bookings Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500">Tidak ada booking yang ditemukan</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pelanggan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Layanan</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tanggal & Waktu</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montir</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{booking.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{booking.customerName}</div>
                      <div className="text-sm text-gray-500">{booking.phone || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-normal">
                      {booking.services && booking.services.length > 0 ? (
                        <div>
                          {booking.services.length > 1 ? (
                            <div>
                              <div className="text-sm font-medium text-gray-900">{booking.services[0]}</div>
                              <div className="text-xs text-primary mt-1">+{booking.services.length - 1} layanan lainnya</div>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-900">{booking.services[0]}</div>
                          )}
                          <div className="text-sm text-gray-500 mt-1">{formatPrice(booking.price)}</div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-sm text-gray-900">{booking.service}</div>
                          <div className="text-sm text-gray-500">{formatPrice(booking.price)}</div>
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        {booking.vehicleMake} {booking.vehicleModel} {booking.vehicleLicensePlate ? `(${booking.vehicleLicensePlate})` : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{booking.date}</div>
                      <div className="text-sm text-gray-500">{booking.time}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${statusColors[booking.status]}`}>
                        {statusLabels[booking.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{booking.mechanicName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewBooking(booking)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100"
                          title="Lihat Detail"
                        >
                          <FiEye size={18} />
                        </button>
                        <button
                          onClick={() => handleEditBooking(booking)}
                          className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-100"
                          title="Edit Booking"
                        >
                          <FiEdit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteBooking(booking)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100"
                          title="Hapus Booking"
                        >
                          <FiTrash2 size={18} />
                        </button>
                        {(!booking.mechanicId || booking.mechanicId === '') && (
                          <button
                            onClick={() => {
                              setSelectedBooking(booking);
                              setModalType('assign');
                              setAssignForm({
                                mechanicId: booking.mechanicId || ''
                              });
                              setIsModalOpen(true);
                            }}
                            className="text-purple-600 hover:text-purple-900 p-1 rounded-full hover:bg-purple-100"
                            title="Tugaskan Montir"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && bookings.length > 0 && renderPagination()}

      {/* Modal Container */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          {modalType === 'view' && renderViewModal()}
          {modalType === 'edit' && renderEditModal()}
          {modalType === 'delete' && renderDeleteModal()}
          {modalType === 'assign' && renderAssignModal()}
        </div>
      )}
    </DashboardLayout>
  );
};

export default AdminBookings; 